import { memo, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { IconBackground } from '@tabler/icons-react';
import { Popover, Transition } from '@headlessui/react';
import tinycolor from 'tinycolor2';
import PlayerMenuTooltip from '../../../PlayerMenus/PlayerMenuTooltip';
import { parsePxValue } from '../utils';
import { ColorPicker } from '@/components/atoms/ColorPicker/ColorPicker';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { brandKit } from '@/stores/brandKit';
import { updateCaptionStyles } from '@/stores/clip';
import { classnames } from '@/libs/utils';
import Toggle from '@/components/atoms/Toggle';

function CaptionBackgroundPopover() {
  const { clipId, clipData } = useClipsContext();
  const brandKitStore = useSyncExternalStore(brandKit.subscribe, brandKit.getSnapshot);

  const [backgroundColor, setBackgroundColor] = useState<string>('#000000');
  const [backgroundOpacity, setBackgroundOpacity] = useState<number>(80);
  const [backgroundPadding, setBackgroundPadding] = useState<number>(16);

  const { enabled, currentColor, currentOpacity, currentPaddingHorizontal } = useMemo(() => {
    const background = clipData.asset_metadata.caption_styles?.pageStyle?.background;
    const currentPaddingHorizontal = parsePxValue(
      clipData.asset_metadata.caption_styles?.pageStyle?.paddingLeft ||
        clipData.asset_metadata.caption_styles?.pageStyle?.paddingRight
    );

    if (!background) {
      return {
        enabled: false,
        currentColor: '#000000',
        currentOpacity: 80,
        currentPaddingHorizontal: currentPaddingHorizontal || 16
      };
    }

    // Use tinycolor2 to parse any color format
    const parsedColor = tinycolor(background);

    if (!parsedColor.isValid()) {
      return {
        enabled: true,
        currentColor: '#000000',
        currentOpacity: 100,
        currentPaddingHorizontal: currentPaddingHorizontal || 16
      };
    }

    const rgbValues = parsedColor.toRgb();
    const hexColor = parsedColor.toHex();
    const alphaPercentage = Math.round(rgbValues.a * 100);

    return {
      enabled: true,
      currentColor: `#${hexColor}`,
      currentOpacity: alphaPercentage,
      currentPaddingHorizontal: currentPaddingHorizontal || 16
    };
  }, [
    clipData.asset_metadata.caption_styles?.pageStyle?.background,
    clipData.asset_metadata.caption_styles?.pageStyle?.paddingLeft,
    clipData.asset_metadata.caption_styles?.pageStyle?.paddingRight
  ]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (currentColor) {
      setBackgroundColor(currentColor);
    }

    setBackgroundOpacity(currentOpacity);
    setBackgroundPadding(currentPaddingHorizontal);
  }, [enabled, currentColor, currentOpacity, currentPaddingHorizontal]);

  const createBackgroundValue = useCallback((color: string, opacity: number) => {
    return tinycolor(color)
      .setAlpha(opacity / 100)
      .toRgbString();
  }, []);

  // ! TODO: @HiteshChoudhary0098 this function needs to be debounced
  const updateBackground = useCallback(
    (newValues: { color?: string; opacity?: number; paddingHorizontal?: number }) => {
      const updatedColor = newValues.color ?? backgroundColor;
      const updatedOpacity = newValues.opacity ?? backgroundOpacity;
      const updatedPadding = newValues.paddingHorizontal ?? backgroundPadding;

      if (newValues.color !== undefined) {
        setBackgroundColor(newValues.color);
      }
      if (newValues.opacity !== undefined) {
        setBackgroundOpacity(newValues.opacity);
      }
      if (newValues.paddingHorizontal !== undefined) {
        setBackgroundPadding(newValues.paddingHorizontal);
      }

      const background = createBackgroundValue(updatedColor, updatedOpacity);

      updateCaptionStyles(clipId, {
        pageStyle: {
          ...clipData.asset_metadata.caption_styles?.pageStyle,
          background,
          paddingLeft: `${updatedPadding}px`,
          paddingRight: `${updatedPadding}px`,
          borderRadius: `${updatedPadding * 0.5}px`
        }
      });
    },
    [
      backgroundColor,
      backgroundOpacity,
      backgroundPadding,
      clipId,
      clipData.asset_metadata.caption_styles?.pageStyle,
      createBackgroundValue
    ]
  );

  const onToggleBackground = useCallback(
    (value: boolean) => {
      if (value) {
        const background = createBackgroundValue(backgroundColor, backgroundOpacity);
        updateCaptionStyles(clipId, {
          pageStyle: {
            ...clipData.asset_metadata.caption_styles?.pageStyle,
            background,
            paddingLeft: `${backgroundPadding}px`,
            paddingRight: `${backgroundPadding}px`,
            borderRadius: `${backgroundPadding * 0.5}px`
          }
        });
      } else {
        updateCaptionStyles(clipId, {
          pageStyle: {
            ...clipData.asset_metadata.caption_styles?.pageStyle,
            background: undefined,
            paddingLeft: undefined,
            paddingRight: undefined
          }
        });
      }
    },
    [
      backgroundColor,
      backgroundOpacity,
      backgroundPadding,
      clipId,
      clipData.asset_metadata.caption_styles?.pageStyle,
      createBackgroundValue
    ]
  );

  const onBackgroundChange = useCallback(
    (value?: string) => {
      if (value) {
        updateBackground({ color: value });
      }
    },
    [updateBackground]
  );

  const onOpacityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateBackground({ opacity: Number(e.target.value) });
    },
    [updateBackground]
  );

  const onPaddingChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateBackground({ paddingHorizontal: Number(e.target.value) });
    },
    [updateBackground]
  );

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="captions-menu-background-button"
            className={classnames(
              'group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100',
              open ? 'bg-slate-100' : 'hover:bg-slate-50'
            )}
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <IconBackground size={16} />
              <PlayerMenuTooltip content="Background" />
            </div>
          </Popover.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel className="absolute z-50 mt-1 w-64 translate-y-1 space-y-5">
              <div className="space-y-4 rounded-lg border bg-white p-4 shadow-lg">
                {/* Enable/Disable Background */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Background</span>
                  <Toggle on={enabled} onToggle={onToggleBackground} />
                </div>

                {enabled && (
                  <>
                    {/* Background Color */}
                    <div className="grid grid-cols-12 items-center justify-between gap-2">
                      <span className="col-span-4 text-sm font-medium">Color</span>
                      <ColorPicker
                        defaultColors={brandKitStore.colors}
                        defaultColorsLabel="Background Color"
                        onChange={onBackgroundChange}
                        hex={backgroundColor}
                        children={
                          <div className="flex size-full items-center justify-between bg-white p-2">
                            <div className="size-6 rounded border" style={{ background: backgroundColor }} />
                            <span className="font-mono text-xs text-slate-400">{backgroundColor}</span>
                          </div>
                        }
                        classNames="relative col-span-8 flex w-full items-center justify-between rounded-md border overflow-hidden"
                      />
                    </div>

                    {/* Opacity */}
                    <div className="grid grid-cols-12 items-center justify-between gap-2">
                      <span className="col-span-4 text-sm font-medium">Opacity</span>
                      <span className="col-span-8 flex items-center gap-2">
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={backgroundOpacity}
                          step={1}
                          onChange={onOpacityChange}
                          className="h-1 w-full cursor-pointer appearance-none rounded-xl bg-slate-300"
                        />
                        <span className="ml-1 w-6 shrink-0 text-right text-2xs tabular-nums text-slate-400">
                          {backgroundOpacity}%
                        </span>
                      </span>
                    </div>

                    {/* Padding */}
                    <div className="grid grid-cols-12 items-center justify-between gap-2">
                      <span className="col-span-4 text-sm font-medium">Padding</span>
                      <span className="col-span-8 flex items-center gap-2">
                        <input
                          type="range"
                          min={0}
                          max={50}
                          value={backgroundPadding}
                          step={1}
                          onChange={onPaddingChange}
                          className="h-1 w-full cursor-pointer appearance-none rounded-xl bg-slate-300"
                        />
                        <span className="ml-1 w-6 shrink-0 text-right text-2xs tabular-nums text-slate-400">
                          {backgroundPadding}px
                        </span>
                      </span>
                    </div>
                  </>
                )}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(CaptionBackgroundPopover);
